import { COMMUNITY_ARTICLE_LOAD_NUMBER } from '@/utils/community-options'
import { useCommunityStore } from '@/store/community'
import Query from '@/utils/query-builder'

export const useCommunity = () => {

  const communityStore = useCommunityStore()
  const baseUrl = useRuntimeConfig().public.goliathUrl
  const client = useFetchClient(baseUrl, 'Goliath API - Community endpoint')
  const config = useRuntimeConfig().public

  const getLanguageHeaders = (locale) => {
    return {
      'Accept-Language': locale
    }
  }

  // This returns all ArticleCategories based on the users diaspora
  const getArticleCategories = async (locale, countryId = '') => {
    if(communityStore.articleCategories[locale].length === 0){
      const resource = `/v1/community/article/categories?country=${countryId}&first=100`
      const response = await client.get(resource, getLanguageHeaders(locale))
      communityStore.setArticleCategories(locale,response.categories)
    }
    return communityStore.articleCategories[locale]
  }

    // This returns all ArticleCategories. It is fetched by the middleware and saved to the store
  const getAllArticleCategories = (locale) => {
    return communityStore.articleCategories[locale]
  }

  const getActiveContent = async () => {
    return communityStore.activeContent
  }

  const getHomeContent = async (locale, countryId = '') => {
    if(communityStore.homeContent.length === 0){
      const resource = `/v1/community/home?web=true&country=${countryId}`
      const response = await client.get(resource, getLanguageHeaders(locale))
      communityStore.setHomeContent(response.content)
    }
    return communityStore.homeContent
  }

  const getCampaignPage = async (locale, slug) => {
    const resource = `/v1/community/web/campaign/${slug}?locale=${locale}`
    const response = await client.get(resource, getLanguageHeaders(locale))
    return response
  }

  const getArticlesByCategorySlug = async (locale, categorySlug, first = 10, skip = 0, countryId = '') => {
    const resource = `/v1/community/articles/category/slug/${categorySlug}?first=${first}&skip=${skip}&country=${countryId}`
    const response = await client.get(resource, getLanguageHeaders(locale))
    const articlesByCatSlug = {
      items: response.articles.map((x) => { return { ...x, imageUrl: x.featuredImageUrl, type: 'article' } }),
      count: response.count
    }
    return articlesByCatSlug
  }

  const getArticleBySlug = async (locale, slug) => {
    if(!communityStore.articlesBySlug[slug]){
      const resource = `/v1/community/article/slug/${slug}`
      const response = await client.get(resource, getLanguageHeaders(locale))
      communityStore.setArticleBySlug(slug, response)
    }
    return communityStore.articlesBySlug[slug]
  }

  const getAllArticles = async (locale, first = 10, skip = 0, countryId = '') => {
    const resource = `/v1/community/articles?first=${first}&skip=${skip}&country=${countryId}`
    const response = await client.get(resource, getLanguageHeaders(locale))
    const allArticles = {
      items: response.articles.map((x) => { return { ...x, imageUrl: x.featuredImageUrl, type: 'article' } }),
      count: response.count
    }
    return allArticles
  }

  const getAllEvents = async (locale, first = 10, skip = 0, countryId = '') => {
    if(communityStore.events[locale].items?.length === 0){
      const resource = `/v1/community/events?first=${first}&skip=${skip}&country=${countryId}`
      const response = await client.get(resource, getLanguageHeaders(locale))
      const events = {
        items: response.events.map((x) => { return { ...x, imageUrl: x.featuredImageUrl, type: 'event' } }),
        count: response.count
      }
      communityStore.events[locale] = events
    }
    return communityStore.events[locale]
  }

  const getEventBySlug = async (locale, slug) => {
    if(!communityStore.eventsBySlug[locale][slug]){
      const resource = `/v1/community/event/slug/${slug}`
      const response = await client.get(resource, getLanguageHeaders(locale))
      communityStore.setEventBySlug(locale,slug,response)
    }
    return communityStore.eventsBySlug[locale][slug]
  }

  const getDiscountBySlug = async (locale, slug) => {
    if(!communityStore.discountsBySlug[locale][slug]){
      const resource = `/v1/community/discount/slug/${slug}`
      const response = await client.get(resource, getLanguageHeaders(locale))
      communityStore.setDiscountBySlug(locale,slug,response)
    }
    return communityStore.discountsBySlug[locale][slug]
  }

  const getAllVideos = async (locale, first = 10, skip = 0, countryId = '') => {
    if(!communityStore.videos[locale].items){
      const resource = `/v1/community/videos?first=${first}&skip=${skip}&country=${countryId}`
      const response = await client.get(resource, getLanguageHeaders(locale))
      const videos = {
        items: response.videos.map((x) => { return { ...x, imageUrl: x.featuredImageUrl, type: 'video' } }),
        count: response.count
      }
      communityStore.setAllVideos(locale,videos)
    }
    return communityStore.videos[locale]
  }

  const getVideoBySlug = async (locale, slug) => {
    if(!communityStore.videosBySlug[locale][slug]){
      const resource = `/v1/community/video/slug/${slug}`
      const response = await client.get(resource, getLanguageHeaders(locale))
      communityStore.setVideoBySlug(locale,slug,response)
    }
    return communityStore.videosBySlug[locale][slug]
  }

  const searchAllByText = async (locale, query, first = 10, skip = 0) => {
    const resource = `/v1/community/search?query=${query}&first=${first}&skip=${skip}`
    const response = await client.get(resource, getLanguageHeaders(locale))
    return response
  }

  const searchAllByTag = async (locale, slug, first = 10, skip = 0) => {
    const resource = `/v1/community/search/tag/slug/${slug}?first=${first}&skip=${skip}`
    const response = await client.get(resource, getLanguageHeaders(locale))
    return response
  }

  const getSurveyPage = async (locale, slug) => {
    const resorce = `/v1/community/web/survey/${slug}?locale=${locale}`
    const response = await client.get(resorce)
    return response
  }

  // This is used in the old SurveyHero component.
  const submitSurvey = async (locale, slug, fields) => {
    const resorce = `/v1/community/web/survey/response?locale=${locale}`
    await client.post(resorce, {
      surveySlug: slug,
      surveyFields: fields
    })
  }

  const graphql = async (query, key) => {
    const resource = config.isProduction || config.isStage
      ? '/v1/community/graphql/live'
      : '/v1/community/graphql/preview'
    const response = await client.get(resource + `?query=${query}&key=${key}`)
    if (!isNullOrUndefined(response.errors)) {
      throw new Error('GraphlQL error')
    }
    return response
  }

//  Functions from store/community

  const getDiscountCategories = async function ( locale ) {
    if(communityStore.discountCategories[locale].length === 0){
      const response = await graphql(`{${discountCategoriesQuery(locale).build}}`, 'community discounts')
      let discountCategories = response ? response.discountCategories : []
      communityStore.setDiscountCategories(locale,discountCategories)
    }
    return communityStore.discountCategories[locale]
  }

  const getCities = async function () {
    if (!communityStore.cities || !communityStore.cities.length || !communityStore.cities.length === 0) {
      const response = await graphql(`{${citiesQuery().build}}`, 'community cities')
      if (response){
        response.cities.length > 0 ? communityStore.setCities(response.cities): communityStore.setCities([])
      } else {
        communityStore.setCities([])
      }
    }
    return communityStore.cities
  }

  const getDiscountsByCategoryCityIds = async function ( categoryId = 'all', cityId = 'all', take, locale ) {
    const params = {
      locale,
      categoryId,
      cityId
    }
    const data = communityStore.discountsByCategoryCityIds[`${categoryId}-${cityId}`]
    const discountLength = (data && data.discounts && data.discounts.length) || 0
    const skip = discountLength || 0

    const response = await graphql(`{${discountsByCategoryCityIdsQuery({ categoryId, cityId, skip, take, locale }).join(' ')}}`, 'community discounts')

    if (skip === 0) {
      communityStore.createDiscountByCategoryCityIds(categoryId, cityId)
      communityStore.setDiscountByCategoryCityIdsCount(categoryId, cityId, response.meta.count)
    }
    communityStore.addDiscountByCategoryCityIds(categoryId, cityId, response.discounts)

    return communityStore.discountsByCategoryCityIds[`${categoryId}-${cityId}`].discounts.slice(0, take)
  }

  const hasMoreDiscountsByCategoryCityIds = async function (asked, { categoryId = 'all', cityId = 'all' }) {
    if (isNullOrUndefined(communityStore.discountsByCategoryCityIds[`${categoryId}-${cityId}`])) {
      return false
    }
    return communityStore.discountsByCategoryCityIds[`${categoryId}-${cityId}`].count > asked
  }
  //  End Functions from store/community

  // Queries

  const discountCategoriesQuery = (locale) => {
    return new Query(
      'allCategoryDiscounts',
      [
        'id',
        'name',
        'slug',
        { name: 'alternatives: _allSlugLocales', fields: ['locale', 'value'] }
      ],
      {
        locale,
        filter: {
          slug: {
            neq: null
          }
        }
      },
      'discountCategories'
    )
  }

  const citiesQuery = () => {
    const fields = [
      'id',
      'name',
      { name: 'state', fields: ['id', 'name', 'iso'] },
      'timezone',
      'stateCode'
    ]
    return new Query(
      'allCities',
      fields,
      {},
      'cities'
    )
  }

  /**
   * This discount query is used in the component Discount List, because we'll only consider cities and categories informed by parameter
   */
  const discountsByCategoryCityIdsQuery = ({ locale, skip = 0, categoryId, take = COMMUNITY_ARTICLE_LOAD_NUMBER, cityId }) => {
    const roundedTime = new Date().setUTCMinutes(0, 0, 0)
    const now = new Date(roundedTime).toISOString()
    const fields = [
      'type: __typename',
      'id',
      'title',
      'description',
      'slug',
      'datetime',
      { name: 'categories', fields: ['name'] },
      { name: 'partner', fields: [{ name: 'city', fields: ['name', 'timezone', 'state { name }'] }, { name: 'logo', fields: ['url'] }] },
      { name: 'cities', fields: ['id', 'name'] }
    ]

    const args = {
      locale,
      orderBy: 'createdAt_DESC',
      first: take,
      skip,
      filter: {
        slug: {
          neq: null
        },
        datetime: {
          gte: now
        }
      }
    }

    if (categoryId && categoryId !== 'all') {
      args.filter.categories = {
        eq: categoryId
      }
    }

    if (cityId && cityId !== 'all') {
      args.filter.cities = {
        anyIn: [cityId]
      }
    }

    const queries = []

    const articlesQuery = new Query('allDiscounts', fields, args, 'discounts')
    queries.push(articlesQuery.build)

    if (skip === 0) {
      const metaArgs = { locale, filter: { slug: { neq: null }, datetime: { gte: now } } }

      if (categoryId && categoryId !== 'all') {
        metaArgs.filter.categories = {
          eq: categoryId
        }
      }

      if (cityId && cityId !== 'all') {
        metaArgs.filter.cities = {
          anyIn: [cityId]
        }
      }

      const meta = new Query('_allDiscountsMeta', ['count'], metaArgs, 'meta')
      queries.push(meta.build)
    }

    return queries
  }

  return {
    getArticleCategories,
    getAllArticleCategories,
    getActiveContent,
    getHomeContent,
    getCampaignPage,
    getArticlesByCategorySlug,
    getArticleBySlug,
    getAllArticles,
    getAllEvents,
    getEventBySlug,
    getDiscountBySlug,
    getAllVideos,
    getVideoBySlug,
    searchAllByText,
    searchAllByTag,
    getSurveyPage,
    submitSurvey,
    graphql,
    getDiscountCategories,
    getCities,
    getDiscountsByCategoryCityIds,
    hasMoreDiscountsByCategoryCityIds
  }
}
